import { inject, Pipe, type PipeTransform } from '@angular/core';

import { type Locale as DateFnsLocale, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { enUS, es } from 'date-fns/locale';

import { I18nService, Locale } from '@core/i18n';

const DATE_FNS_LOCALES: Record<Locale, DateFnsLocale> = {
  [Locale.English]: enUS,
  [Locale.Spanish]: es,
};

@Pipe({
  name: 'mlTimeAgo',
  pure: false,
})
export class TimeAgoPipe implements PipeTransform {
  private readonly i18n = inject(I18nService);

  transform(value: string | number | Date | null | undefined): string {
    const locale = DATE_FNS_LOCALES[this.i18n.currentLang()];
    const date = this.parse(value);

    if (!date) {
      return value == null ? '' : String(value);
    }

    return formatDistanceToNow(date, { addSuffix: true, locale });
  }

  private parse(value: string | number | Date | null | undefined): Date | null {
    if (value == null || value === '') {
      return null;
    }

    if (value instanceof Date) {
      return isValid(value) ? value : null;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      const timestamp = value < 1e12 ? value * 1000 : value;
      const date = new Date(timestamp);

      return isValid(date) ? date : null;
    }

    const raw = String(value).trim();

    if (!raw) {
      return null;
    }

    if (/^\d+$/.test(raw)) {
      return this.parse(Number(raw));
    }

    const iso = raw.includes('T') ? raw : raw.replace(' ', 'T');
    const withZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(iso)
      ? iso
      : iso.length === 10
        ? `${iso}T00:00:00Z`
        : `${iso}Z`;
    const date = parseISO(withZone);

    return isValid(date) ? date : null;
  }
}
